//* Libraries imports
import {
  DollarSignIcon,
  PercentIcon,
  HashIcon,
  CreditCardIcon,
  CoinsIcon,
  ReceiptIcon,
  PiggyBankIcon,
} from "lucide-react";

//* Componentes imports
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { NumericInput } from "@/components/ui/numeric-input";
import { Required } from "@/components/ui/required";

//* Schemas imports
import {
  MethodPaymentEnum,
  MethodPaymentEnumLabel,
} from "@/schemas/methodpayment";

//* Utils imports
import {
  PAYMENT_METHOD_ICONS,
  PAYMENT_METHOD_COLORS,
} from "@/helpers/paymentHelper";

//* Types imports
import type { IFinancialConfig } from "@/features/Element/types";
import type { ApiError } from "@/types/api";

type FinancialConfigFormProps = {
  config: IFinancialConfig | undefined;
  onChange: (config: IFinancialConfig) => void;
  title?: string;
  error?: ApiError | null;
  onlyPaymentMethods?: boolean;
};

export function FinancialConfigForm({
  config = {},
  onChange,
  title = "Configuração Financeira",
  error,
  onlyPaymentMethods = false,
}: FinancialConfigFormProps) {
  const handleChange = <K extends keyof IFinancialConfig>(
    field: K,
    value: IFinancialConfig[K],
  ) => {
    onChange({
      ...config,
      [field]: value,
    });
  };

  const handlePaymentMethodChange = (
    method: MethodPaymentEnum,
    checked: boolean,
  ) => {
    const currentMethods = config.paymentMethods || [];
    let newMethods: MethodPaymentEnum[];

    if (checked) {
      newMethods = [...currentMethods, method];
    } else {
      newMethods = currentMethods.filter((m) => m !== method);
    }

    handleChange("paymentMethods", newMethods);
  };

  return (
    <section className="flex flex-col w-full items-start rounded-xl mt-4">
      <Card className="w-full border border-[#D8E0F0] rounded-[14px] bg-[#FCFCFC] hover:border-[#c8d0e0] transition-colors">
        <CardContent className="p-[20px]">
          <div className="flex w-full items-center justify-between mb-5">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <CoinsIcon size={24} className="text-blue-500" />
                <Label className="text-xl font-bold">
                  {title} <Required />
                </Label>
              </div>
              {error?.validationErrors?.financialConfig && (
                <p className="text-sm text-red-500">
                  {error.validationErrors.financialConfig.message}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex flex-col gap-2">
              <Label htmlFor="cashPrice" className="flex items-center gap-1">
                <DollarSignIcon size={14} className="text-gray-500" />
                Preço à Vista <Required />
              </Label>
              <NumericInput
                id="cashPrice"
                variant="currency"
                placeholder="R$ 0,00"
                value={config.cashPrice}
                onChange={(val) => handleChange("cashPrice", val)}
                disabled={onlyPaymentMethods}
                className={`rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                  error?.validationErrors?.financialConfig && !config.cashPrice
                    ? "border-red-500"
                    : ""
                }`}
              />
              {error?.validationErrors?.financialConfig &&
                !config.cashPrice && (
                  <p className="text-xs text-red-500">
                    O preço à vista é obrigatório
                  </p>
                )}
            </div>
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="installmentPrice"
                className="flex items-center gap-1"
              >
                <ReceiptIcon size={14} className="text-gray-500" />
                Preço Parcelado <Required />
              </Label>
              <NumericInput
                id="installmentPrice"
                variant="currency"
                placeholder="R$ 0,00"
                value={config.installmentPrice}
                onChange={(val) => handleChange("installmentPrice", val)}
                disabled={onlyPaymentMethods}
                className={`rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                  error?.validationErrors?.financialConfig &&
                  !config.installmentPrice
                    ? "border-red-500"
                    : ""
                }`}
              />
              {error?.validationErrors?.financialConfig &&
                !config.installmentPrice && (
                  <p className="text-xs text-red-500">
                    O preço parcelado é obrigatório
                  </p>
                )}
            </div>
          </div>

          <div className="mb-6 flex flex-col gap-2">
            <Label className="mb-1 font-semibold text-gray-700 flex items-center gap-1">
              <CreditCardIcon size={16} className="text-gray-500" />
              Métodos de Pagamento <Required />
            </Label>
            <div
              className={`flex flex-wrap justify-center gap-4 p-4 border rounded-xl bg-white shadow-sm ${
                error?.validationErrors?.financialConfig &&
                (!config.paymentMethods || config.paymentMethods.length === 0)
                  ? "border-red-500"
                  : "border-gray-100"
              }`}
            >
              {Object.entries(MethodPaymentEnumLabel).map(([value, label]) => {
                const enumValue = value as MethodPaymentEnum;
                const Icon = PAYMENT_METHOD_ICONS[enumValue] || CreditCardIcon;
                const colorClasses = PAYMENT_METHOD_COLORS[enumValue] || "";
                const isChecked =
                  config.paymentMethods?.includes(enumValue) || false;

                return (
                  <div
                    key={value}
                    className={`flex items-center gap-3 p-2 rounded-lg border transition-all ${
                      isChecked
                        ? colorClasses
                        : "bg-gray-50 border-gray-100 text-gray-500 grayscale opacity-70"
                    }`}
                  >
                    <Checkbox
                      id={`method-${value}`}
                      checked={isChecked}
                      onCheckedChange={(checked) =>
                        handlePaymentMethodChange(enumValue, checked === true)
                      }
                      className={isChecked ? "border-current" : ""}
                    />
                    <Label
                      htmlFor={`method-${value}`}
                      className="text-sm font-semibold flex items-center gap-2 cursor-pointer"
                    >
                      <Icon size={16} />
                      {label}
                    </Label>
                  </div>
                );
              })}
            </div>
            {error?.validationErrors?.financialConfig &&
              (!config.paymentMethods ||
                config.paymentMethods.length === 0) && (
                <p className="text-xs text-red-500">
                  Selecione pelo menos um método de pagamento
                </p>
              )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="maxInstallments"
                className="flex items-center gap-1"
              >
                <HashIcon size={14} className="text-gray-500" />
                Máximo de Parcelas <Required />
              </Label>
              <NumericInput
                id="maxInstallments"
                variant="integer"
                placeholder="1"
                value={config.maxInstallments}
                onChange={(val) => handleChange("maxInstallments", val)}
                disabled={onlyPaymentMethods}
                className={`rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                  error?.validationErrors?.financialConfig &&
                  !config.maxInstallments
                    ? "border-red-500"
                    : ""
                }`}
                suffix={<span className="text-xs font-medium">x</span>}
              />
              {error?.validationErrors?.financialConfig &&
                !config.maxInstallments && (
                  <p className="text-xs text-red-500">Campo obrigatório</p>
                )}
            </div>
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="interestFreeInstallments"
                className="flex items-center gap-1"
              >
                <PiggyBankIcon size={14} className="text-gray-500" />
                Parcelas sem Juros <Required />
              </Label>
              <NumericInput
                id="interestFreeInstallments"
                variant="integer"
                placeholder="0"
                value={config.interestFreeInstallments}
                onChange={(val) =>
                  handleChange("interestFreeInstallments", val)
                }
                disabled={onlyPaymentMethods}
                className={`rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                  error?.validationErrors?.financialConfig &&
                  config.interestFreeInstallments === undefined
                    ? "border-red-500"
                    : ""
                }`}
                suffix={<span className="text-xs font-medium">x</span>}
              />
              {error?.validationErrors?.financialConfig &&
                config.interestFreeInstallments === undefined && (
                  <p className="text-xs text-red-500">Campo obrigatório</p>
                )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="interestRate" className="flex items-center gap-1">
                <PercentIcon size={14} className="text-gray-500" />
                Taxa de Juros (%) <Required />
              </Label>
              <NumericInput
                id="interestRate"
                variant="percentage"
                placeholder="0,0"
                value={config.interestRate}
                onChange={(val) => handleChange("interestRate", val)}
                disabled={onlyPaymentMethods}
                className={`rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                  error?.validationErrors?.financialConfig &&
                  config.interestRate === undefined
                    ? "border-red-500"
                    : ""
                }`}
                suffix={<span className="text-xs font-medium">%</span>}
              />
              {error?.validationErrors?.financialConfig &&
                config.interestRate === undefined && (
                  <p className="text-xs text-red-500">Campo obrigatório</p>
                )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="standardCashDiscount"
                className="flex items-center gap-1"
              >
                <PercentIcon size={14} className="text-gray-500" />
                Desconto à Vista Padrão (%) <Required />
              </Label>
              <NumericInput
                id="standardCashDiscount"
                variant="percentage"
                placeholder="0,0"
                value={config.standardCashDiscount}
                onChange={(val) => handleChange("standardCashDiscount", val)}
                disabled={onlyPaymentMethods}
                className={`rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                  error?.validationErrors?.financialConfig &&
                  config.standardCashDiscount === undefined
                    ? "border-red-500"
                    : ""
                }`}
                suffix={<span className="text-xs font-medium">%</span>}
              />
              {error?.validationErrors?.financialConfig &&
                config.standardCashDiscount === undefined && (
                  <p className="text-xs text-red-500">Campo obrigatório</p>
                )}
            </div>
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="standardInstallmentDiscount"
                className="flex items-center gap-1"
              >
                <PercentIcon size={14} className="text-gray-500" />
                Desconto Parcelado Padrão (%) <Required />
              </Label>
              <NumericInput
                id="standardInstallmentDiscount"
                variant="percentage"
                placeholder="0,0"
                value={config.standardInstallmentDiscount}
                onChange={(val) =>
                  handleChange("standardInstallmentDiscount", val)
                }
                disabled={onlyPaymentMethods}
                className={`rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                  error?.validationErrors?.financialConfig &&
                  config.standardInstallmentDiscount === undefined
                    ? "border-red-500"
                    : ""
                }`}
                suffix={<span className="text-xs font-medium">%</span>}
              />
              {error?.validationErrors?.financialConfig &&
                config.standardInstallmentDiscount === undefined && (
                  <p className="text-xs text-red-500">Campo obrigatório</p>
                )}
            </div>
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="managerOverrideDiscount"
                className="flex items-center gap-1"
              >
                <PercentIcon size={14} className="text-gray-500" />
                Desconto Extra Gerente (%) <Required />
              </Label>
              <NumericInput
                id="managerOverrideDiscount"
                variant="percentage"
                placeholder="0,0"
                value={config.managerOverrideDiscount}
                onChange={(val) => handleChange("managerOverrideDiscount", val)}
                disabled={onlyPaymentMethods}
                className={`rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                  error?.validationErrors?.financialConfig &&
                  config.managerOverrideDiscount === undefined
                    ? "border-red-500"
                    : ""
                }`}
                suffix={<span className="text-xs font-medium">%</span>}
              />
              {error?.validationErrors?.financialConfig &&
                config.managerOverrideDiscount === undefined && (
                  <p className="text-xs text-red-500">Campo obrigatório</p>
                )}
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
